import { DNode } from '@dojo/framework/core/interfaces';
import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';

import { pathmap, registry, keymap, createNlsMiddleware, userstate } from 'lcc-state';
import { TopicDetails, ParentTree } from 'lcc-state/interfaces';

import { TopicProperties } from '@holmescorp/book-widgets/interfaces';

import { Layout, LearnerTools, ContentContainer, Breadcrumbs, MicroProgress } from './index';
import GroupNav from './panels/group-nav';
import MicroToolsButton from './tools-button';

import css from '../css/topics.m.css';

export interface TopicGroupProperties extends TopicProperties {
	parent: TopicDetails;
}

const factory = create({
	theme,
	nls: createNlsMiddleware('ditabook'),
	userstate
}).properties<TopicGroupProperties>();

export const TopicGroup = factory(({ id: instanceId, properties, middleware: { theme, nls, userstate } }): DNode => {
	const { topic, parent: topicset } = properties();
	const {
		messages: {
			micro_tgf_quiz_label = 'Take Topic Group Family Quiz',
			micro_tgf_flashcards_label = 'Module Flashcards'
		}
	} = nls();

	const themeCss = theme.classes(css);
	const status: ParentTree = userstate.get(userstate.path('status'));

	const id = 'topicgroup-wrapper';
	const widgetId = `${id}-${instanceId}`;

	const tgfKey = topicset.attributes?.key || '';
	const currentTopicGroupKey = topic.attributes?.key;

	const topicGroups = topicset.children?.filter((tg) => !('shortdesc' in tg)) || [];
	const total = topicGroups.length;
	const complete = topicGroups
		.map((tg) => ({
			attributes: tg.topicgroup.attributes,
			progress: status[tg.topicgroup.attributes?.key || ''].data
		}))
		.filter((tg) => tg.progress.complete === tg.progress.total).length;
	const label = v('span', {}, [`Reading Progress`]);

	const { path: topicGroupPath } = keymap.get(tgfKey)!;
	const hasFlashcards = 'view' in (pathmap.get(`${topicGroupPath}/flashcards`)! || {});

	return v('div', { id: `${id}-main` }, [
		v(
			'div',
			{
				id,
				widgetId,
				key: widgetId,
				xqa: `${id}-layout`
			},
			[
				w(Breadcrumbs, {}, [
					w(MicroProgress, {
						max: total,
						value: complete,
						label,
						additionalClasses: [themeCss.progressContainer]
					})
				]),
				w(
					Layout,
					{
						xqa: `${id}-layout-widget`,
						id: `${id}-layout-widget-${instanceId}`,
						key: `${id}-two-column-layout`
					},
					[
						{
							leading: [w(ContentContainer, {}, [w('TopicFull', { topic })])],
							trailing: [
								w(LearnerTools, { includeQuizButton: false }),
								w(GroupNav, { currentTopicGroupKey, topicset }),
								hasFlashcards
									? v('div', { classes: [themeCss.actionButton] }, [
											w(MicroToolsButton, {
												labelText: micro_tgf_flashcards_label,
												onClick: () => {
													const { view } = pathmap.get(`${topicGroupPath}/flashcards`)!;
													view();
												},
												onDown: () => {
													const { view } = pathmap.get(`${topicGroupPath}/flashcards`)!;
													view();
												}
											})
									  ])
									: null,
								v('div', { classes: [themeCss.actionButton] }, [
									w(MicroToolsButton, {
										labelText: micro_tgf_quiz_label,
										onClick: () => {
											const { view } = pathmap.get(`${topicGroupPath}/quiz`)!;
											view();
										},
										onDown: () => {
											const { view } = pathmap.get(`${topicGroupPath}/quiz`)!;
											view();
										}
									})
								])
							]
						}
					]
				)
			]
		)
	]);
});

registry.define('MicroTopicGroup', TopicGroup);

export default TopicGroup;
